/**
 * The floating-island chrome: muted ground, rail card, canvas card.
 *
 * Copied structurally from 8loop's `main-layout.tsx` (research/design-system.md
 * §3.1) so this app reads as part of the same product — 8px gutter, 8px gap, no
 * desktop topbar, both surfaces rounded cards on `bg-muted`.
 *
 * The rail is account-aware: the account-scoped items only appear once an account
 * is open, because "Queue" with no account selected has nowhere to go. The Queue
 * badge is the due-now count, which is the one number an FDE opens this app for.
 *
 * The account's pages sit under a section label naming the account, and are
 * indented beneath it. Flat, they read as peers of "Accounts" — which is
 * exactly the confusion Arun reported ("clearlty inside accounts … but it looks
 * liek separate left nav items"). Configuration is one of them, in the main
 * list: it used to live in `bottomItems`, pinned to the floor of the rail with the
 * user block, which is how it got missed. CRM outbound is another, admin-only.
 */

import { useMemo, useState, type ComponentType, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  Check,
  ChevronsUpDown,
  DatabaseZap,
  Loader2,
  LogOut,
  Menu,
  MonitorSmartphone,
  Moon,
  PhoneOutgoing,
  Settings,
  ShieldBan,
  Sun,
} from "lucide-react";
import { NavRail, type NavRailEntry } from "@/design-system/organisms/nav-rail/nav-rail";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import glyphSrc from "@/design-system/organisms/nav-rail/brand-glyph.svg";
import wordmarkSrc from "@/design-system/organisms/nav-rail/wordmark.svg";
import { useTheme, type Theme } from "@/hooks/use-theme";
import { errorMessage } from "@/lib/api";
import { useAccounts, useLogout, useMe } from "@/lib/queries";

/** react-router-aware anchor for NavRail, whose items are plain `href`s. */
function RailLink({
  href,
  children,
  ...rest
}: {
  href: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}

export type ShellNavId = "accounts" | "crm" | "queue" | "suppressions" | "settings";

export function AppShell({ active, children }: { active: ShellNavId; children: ReactNode }) {
  const { accountId } = useParams<{ accountId?: string }>();
  const { data: me } = useMe();
  const { data: accountsData } = useAccounts();
  const [mobileOpen, setMobileOpen] = useState(false);

  const account = accountsData?.accounts.find((a) => a.id === accountId);
  const dueNow = account?.queue.dueNow ?? 0;
  const isAdmin = me?.user?.is_admin ?? false;

  const items = useMemo<NavRailEntry[]>(() => {
    const list: NavRailEntry[] = [
      { id: "accounts", label: "Accounts", icon: Building2, href: "/accounts" },
    ];
    if (accountId) {
      list.push({
        kind: "section",
        id: "account-section",
        // The account's own name is the heading, so the three items below it read
        // as "this account's queue", not "the app's queue".
        label: account?.name ?? "This account",
        sublabel: account?.tenant_host,
      });
      list.push({
        id: "queue",
        label: "Callback queue",
        icon: PhoneOutgoing,
        href: `/accounts/${accountId}/queue`,
        // Only shown when there is something to act on: a permanent "0" trains
        // people to stop reading the number.
        badge: dueNow > 0 ? dueNow : undefined,
        nested: true,
      });
      list.push({
        id: "suppressions",
        label: "Do not call",
        icon: ShieldBan,
        href: `/accounts/${accountId}/suppressions`,
        nested: true,
      });
      /*
        CRM outbound belongs to the account, and an account may have several feeds. Arun:
        *"the crm outbound is also per customer account and each customer account can have
        more than one of these can you design it that way please."* It sat at the top level
        as a peer of Accounts until then, which said the opposite.

        Still its own page rather than a tab of the callback queue, per M3: nobody on a CRM
        list asked to be rung, so the two lists cannot share one screen.

        Admins only: every `/api/orchestrator` route is `requireAdmin`, so for anyone else
        this item can only lead to a 403, and a rail entry guaranteed to fail is worse than
        no entry.
      */
      if (isAdmin) {
        list.push({
          id: "crm",
          label: "CRM outbound",
          icon: DatabaseZap,
          href: `/accounts/${accountId}/crm`,
          nested: true,
        });
      }
      list.push({
        id: "settings",
        label: "Configuration",
        icon: Settings,
        href: `/accounts/${accountId}/settings`,
        nested: true,
      });
    }
    return list;
  }, [accountId, account?.name, account?.tenant_host, dueNow, isAdmin]);

  const rail = (storageKey: string | null) => (
    <NavRail
      items={items}
      activeId={active}
      storageKey={storageKey}
      linkComponent={RailLink}
      brandWordmarkSrc={wordmarkSrc}
      brandGlyphSrc={glyphSrc}
      user={
        me?.user
          ? { name: me.user.name ?? me.user.email, email: me.user.email }
          : undefined
      }
      userSlot={
        me?.user ? (
          <UserMenu name={me.user.name ?? me.user.email} email={me.user.email} />
        ) : undefined
      }
      ariaLabel="Callback app navigation"
    />
  );

  return (
    <div className="flex h-screen overflow-hidden gap-2 p-2 bg-muted">
      <div className="hidden md:contents">{rail("8loop:navrail:collapsed")}</div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-auto p-2">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          {rail(null)}
        </SheetContent>
      </Sheet>

      {/*
        A flex column rather than 8loop's plain `overflow-auto` canvas, because
        this app adds a mobile-only bar above the page: pages size themselves with
        `h-full`, so without the column the bar's height pushed the bottom of every
        page — the pagination bar — off screen on a phone.
      */}
      <main className="flex flex-1 min-w-0 flex-col overflow-hidden bg-background border border-border rounded-2xl">
        <div className="flex shrink-0 items-center gap-2 px-4 py-3 border-b border-border md:hidden">
          <button
            type="button"
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
            className="inline-flex size-8 items-center justify-center rounded-md hover:bg-accent"
          >
            <Menu size={18} />
          </button>
          <span className="text-sm font-medium">{account?.name ?? "Callbacks"}</span>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">{children}</div>
      </main>
    </div>
  );
}

/**
 * The rail's user block is a button; this is what it opens.
 *
 * It reuses NavRail's own `rail-user` markup and classes rather than inventing a
 * trigger, because `userSlot` *replaces* the built-in block — anything else here
 * and the bottom of the rail stops looking like 8loop's.
 *
 * Theme lives here rather than in Configuration because it is a property of this
 * browser, not of the account — putting it on an account settings page would imply
 * it travelled with the tenant.
 */
function UserMenu({ name, email }: { name: string; email: string }) {
  const navigate = useNavigate();
  const logout = useLogout();
  const { theme, setTheme } = useTheme();

  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join("") || "?";

  const themes: { value: Theme; label: string; icon: ComponentType<{ size?: number }> }[] = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: MonitorSmartphone },
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="rail-user" aria-label="Account and appearance">
          <div className="av" aria-hidden="true">
            {initials}
          </div>
          <div className="info">
            <span className="name">{name}</span>
            <span className="email">{email}</span>
          </div>
          <ChevronsUpDown className="chev" size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="end" className="w-56 p-1">
        <div className="px-2 py-1.5 text-[11px] font-medium text-muted-foreground">Theme</div>
        {/*
          The tick, not the weight, is what says which theme is on.

          `font-medium` was carrying that alone, and hovering any of the three rows gave
          it a background — so a hovered "Light" out-ranked a selected "Dark" visually,
          and the honest answer to "which theme am I on?" was invisible the moment the
          pointer entered the menu. A mark that does not move on hover cannot be
          out-ranked by one.
        */}
        {themes.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTheme(t.value)}
            aria-pressed={theme === t.value}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent ${
              theme === t.value ? "font-medium" : ""
            }`}
          >
            <t.icon size={14} />
            {t.label}
            {theme === t.value && <Check size={14} className="ml-auto text-primary" />}
          </button>
        ))}
        <div className="my-1 h-px bg-border" />
        {/*
          Signing out is a network call that can fail, and this button used to swallow
          both facts: it stayed fully clickable while in flight — a second click fires a
          second logout — and an error left the label reading "Sign out" with nothing to
          say the session is still open. The label already changes; now the button
          actually stops taking clicks, and says so if it fails.
        */}
        <button
          type="button"
          disabled={logout.isPending}
          onClick={() =>
            logout.mutate(undefined, {
              onSuccess: () => navigate("/login", { replace: true }),
              onError: (e) => toast.error(`Couldn't sign out: ${errorMessage(e)}`),
            })
          }
          className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
        >
          {logout.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <LogOut size={14} />
          )}
          {logout.isPending ? "Signing out…" : "Sign out"}
        </button>
      </PopoverContent>
    </Popover>
  );
}
